import React from 'react';
import { Field, Select, Label, Description } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

interface SelectOption {
  value: string;
  label: string;
}

interface FormSelectProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLSelectElement>) => void;
  options: SelectOption[];
  error?: string;
  touched?: boolean;
  required?: boolean;
  description?: string;
  placeholder?: string;
  className?: string;
}

const FormSelect: React.FC<FormSelectProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  options,
  error,
  touched,
  required = false,
  description,
  placeholder,
  className,
}) => {
  return (
    <Field className={className}>
      <Label className="text-base font-semibold text-slate-700 mb-2 block">
        {label} {required && '*'}
      </Label>
      {description && (
        <Description className="text-sm text-slate-600 mb-2 block leading-relaxed">
          {description}
        </Description>
      )}
      <div className="relative">
        <Select
          id={id}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          value={value}
          required={required}
          className={clsx(
            'border border-slate-300 block w-full appearance-none rounded-lg bg-white py-3 px-4 text-base shadow-sm text-slate-900',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            'transition duration-150 ease-in-out',
            '*:text-slate-900'
          )}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <ChevronDownIcon
          className="group pointer-events-none absolute top-3.5 right-3 size-5 fill-slate-400"
          aria-hidden="true"
        />
      </div>
      {touched && error ? (
        <div className="text-red-700 text-sm mt-2 bg-red-50 border border-red-200 p-2 rounded">
          {error}
        </div>
      ) : null}
    </Field>
  );
};

export default FormSelect;
